/**
 * MODULE: _shared/frontend-shell/src/design-system/components/Feedback/Toast.tsx
 * PURPOSE: Display temporary notifications with auto-dismiss and action buttons
 *
 * DEPENDENCIES:
 *   - flowbite-react: Alert, Button components
 *   - @heroicons/react: CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon
 *   - React: useState, useEffect
 *
 * PUBLIC API:
 *   - Toast: Individual toast component
 *   - ToastProps: from **`toast.types`**
 *
 * SIDE EFFECTS:
 *   - React rendering
 *   - setTimeout for auto-dismiss
 *
 * AGENT SAFETY: 🟢 SAFE - Presentational component
 * FILE SIZE: 120 / 300 lines
 *
 * See: /README.md | /ARCHITECTURE.md | /_shared/frontend-shell/README.md
 *
 * RECENT: 2026-05-04 — Optional **`toast.testId`** → root **`data-testid`** (Settings OAuth return toasts).
 */

import { Alert, Button } from 'flowbite-react';
import { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { ToastProps } from './toast.types';

export function Toast({ toast, onDismiss }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss(toast.id), 300); // Wait for fade-out animation
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [toast.id, toast.duration, onDismiss]);

  const getIconComponent = () => {
    switch (toast.variant) {
      case 'success':
        return CheckCircleIcon;
      case 'error':
        return ExclamationCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      case 'info':
      default:
        return InformationCircleIcon;
    }
  };

  const getColor = (): 'success' | 'failure' | 'warning' | 'info' => {
    switch (toast.variant) {
      case 'success':
        return 'success';
      case 'error':
        return 'failure';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className={`mb-4 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
      data-testid={toast.testId}
    >
      <Alert
        color={getColor()}
        icon={getIconComponent()}
        onDismiss={() => {
          setIsVisible(false);
          setTimeout(() => onDismiss(toast.id), 300);
        }}
      >
        <div className="flex items-center justify-between">
          <span className="font-medium">{toast.message}</span>
          {toast.action && (
            <Button
              size="xs"
              color="gray"
              onClick={() => {
                toast.action?.onClick();
                setIsVisible(false);
                setTimeout(() => onDismiss(toast.id), 300);
              }}
              className="ml-4"
            >
              {toast.action.label}
            </Button>
          )}
        </div>
      </Alert>
    </div>
  );
}
